import React, { useState, useEffect } from 'react';
import api from '../api';

const LICENCE_TYPES = [
  'RSA', 'RSG', 'Food Handler', 'Forklift', 'White Card',
  'First Aid', 'Security Licence', 'Driver Licence', 'Working with Children'
];

function maskLicenceNumber(num) {
  if (!num || num.length < 4) return num || '';
  return '****' + num.slice(-4);
}

function StatusBadge({ status }) {
  if (status === 'valid') return <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-semibold border border-green-200">✅ Valid</span>;
  if (status === 'expiring_soon') return <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-semibold border border-amber-200">⚠️ Expiring Soon</span>;
  if (status === 'expired') return <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-semibold border border-red-200">❌ Expired</span>;
  return null;
}

const BLANK_FORM = { licence_type: LICENCE_TYPES[0], licence_number: '', expiry_date: '', issuing_body: '' };

export default function LicencesPage({ user, onBack }) {
  const http = api();
  const [licences, setLicences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [toast, setToast] = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const load = async () => {
    try {
      const res = await http.get('/worker/licences');
      setLicences(res.data || []);
    } catch { setLicences([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await http.post('/worker/licences', form);
      showToast('Licence added ✅');
      setShowForm(false);
      setForm(BLANK_FORM);
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to add licence');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await http.delete(`/worker/licences/${id}`);
      showToast('Licence removed');
      load();
    } catch { showToast('Failed to delete'); }
    finally { setDeletingId(null); }
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        {onBack && (
          <button onClick={onBack} className="p-2 rounded-xl hover:bg-neutral-100 transition">
            <svg className="w-5 h-5 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
        )}
        <div className="flex-1">
          <h2 className="font-bold text-neutral-900 text-lg">My Licences & Certifications 🪪</h2>
          <p className="text-xs text-neutral-400">Keep your licences up to date to get more shifts</p>
        </div>
      </div>

      {toast && (
        <div className="mb-4 p-3 rounded-xl text-sm bg-brand-50 border border-brand-200 text-brand-700">{toast}</div>
      )}

      {/* Add button */}
      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="w-full mb-5 py-3 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-xl text-sm transition shadow-sm"
        >
          + Add Licence
        </button>
      )}

      {/* Add form */}
      {showForm && (
        <form onSubmit={handleAdd} className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-5 mb-5">
          <h3 className="font-bold text-neutral-900 mb-4">Add Licence</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Licence Type *</label>
              <select
                value={form.licence_type}
                onChange={e => setForm(f => ({ ...f, licence_type: e.target.value }))}
                className="w-full px-3 py-2.5 bg-white border border-neutral-200 rounded-xl text-sm focus:outline-none focus:border-brand-400"
              >
                {LICENCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Licence Number</label>
              <input
                type="text"
                value={form.licence_number}
                onChange={e => setForm(f => ({ ...f, licence_number: e.target.value }))}
                placeholder="e.g. RSA-12345678"
                className="w-full px-3 py-2.5 bg-white border border-neutral-200 rounded-xl text-sm focus:outline-none focus:border-brand-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Expiry Date</label>
              <input
                type="date"
                value={form.expiry_date}
                onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))}
                className="w-full px-3 py-2.5 bg-white border border-neutral-200 rounded-xl text-sm focus:outline-none focus:border-brand-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Issuing Body</label>
              <input
                type="text"
                value={form.issuing_body}
                onChange={e => setForm(f => ({ ...f, issuing_body: e.target.value }))}
                placeholder="e.g. NSW OLGR, Safe Work NSW"
                className="w-full px-3 py-2.5 bg-white border border-neutral-200 rounded-xl text-sm focus:outline-none focus:border-brand-400"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button type="submit" disabled={saving}
              className="flex-1 py-2.5 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-xl text-sm transition disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Licence'}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setForm(BLANK_FORM); }}
              className="px-4 py-2.5 border border-neutral-200 text-neutral-600 rounded-xl text-sm hover:bg-neutral-50 transition">
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Licences list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : licences.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-neutral-100 shadow-sm">
          <div className="text-4xl mb-3">🪪</div>
          <p className="font-medium text-neutral-600 mb-1">No licences added yet</p>
          <p className="text-sm text-neutral-400">Add your licences to unlock more shifts</p>
        </div>
      ) : (
        <div className="space-y-3">
          {licences.map(lic => (
            <div key={lic.id} className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="font-bold text-neutral-900">{lic.licence_type}</p>
                    <StatusBadge status={lic.status} />
                  </div>
                  {lic.licence_number && (
                    <p className="text-xs text-neutral-500">Number: {maskLicenceNumber(lic.licence_number)}</p>
                  )}
                  {lic.expiry_date && (
                    <p className="text-xs text-neutral-500">
                      Expires: {new Date(lic.expiry_date).toLocaleDateString('en-AU')}
                    </p>
                  )}
                  {lic.issuing_body && (
                    <p className="text-xs text-neutral-400">Issued by: {lic.issuing_body}</p>
                  )}
                </div>
                <button
                  onClick={() => handleDelete(lic.id)}
                  disabled={deletingId === lic.id}
                  className="p-2 text-neutral-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                  title="Remove licence"
                >
                  {deletingId === lic.id ? (
                    <div className="w-4 h-4 border-2 border-red-300 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info footer */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-3">
        <p className="text-xs text-blue-700 font-medium mb-1">Why add your licences?</p>
        <p className="text-xs text-blue-600">Employers can filter for workers with specific licences (RSA, White Card, Forklift, etc). Adding yours makes you visible for more shifts and higher-paying roles.</p>
      </div>
    </div>
  );
}
